import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Filter, Play, Plus, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'

interface SmartList {
  id: string
  name: string
  filtersJson: unknown
  contactCountCached: number
  lastCountedAt: string | null
  updatedAt: string
}

interface PreviewResult {
  count: number
  sample: Array<{
    id: string
    displayName: string
    email: string | null
    phoneE164: string | null
    lastActivityAt: string | null
  }>
}

const EXAMPLE_FILTERS = JSON.stringify(
  [
    { type: 'IN_LIFECYCLE', name: 'hot' },
    { type: 'CONSENT_FOR', channel: 'EMAIL' },
  ],
  null,
  2,
)

export function SmartListsPage() {
  const [items, setItems] = useState<SmartList[]>([])
  const [selected, setSelected] = useState<SmartList | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftFilters, setDraftFilters] = useState(EXAMPLE_FILTERS)
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    const r = await api<{ items: SmartList[] }>('/v1/marketing/smart-lists')
    setItems(r.items)
  }

  useEffect(() => {
    void load()
  }, [])

  function pick(sl: SmartList) {
    setSelected(sl)
    setDraftName(sl.name)
    setDraftFilters(JSON.stringify(sl.filtersJson, null, 2))
    setPreview(null)
    setErr(null)
  }

  function newOne() {
    setSelected(null)
    setDraftName('')
    setDraftFilters(EXAMPLE_FILTERS)
    setPreview(null)
    setErr(null)
  }

  async function runPreview() {
    setBusy(true)
    setErr(null)
    try {
      const parsed = JSON.parse(draftFilters)
      const r = await api<PreviewResult>('/v1/marketing/smart-lists/preview-adhoc', {
        method: 'POST',
        body: JSON.stringify({ filtersJson: parsed }),
      })
      setPreview(r)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    setBusy(true)
    setErr(null)
    try {
      const parsed = JSON.parse(draftFilters)
      const body = JSON.stringify({ name: draftName, filtersJson: parsed })
      if (selected) {
        await api(`/v1/marketing/smart-lists/${selected.id}`, { method: 'PATCH', body })
      } else {
        await api(`/v1/marketing/smart-lists`, { method: 'POST', body })
      }
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(sl: SmartList) {
    if (!confirm(`¿Eliminar la SmartList "${sl.name}"?`)) return
    await api(`/v1/marketing/smart-lists/${sl.id}`, { method: 'DELETE' })
    if (selected?.id === sl.id) newOne()
    await load()
  }

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Filter className="size-5 text-blueprint-700" />
          SmartLists
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          DSL JSON · operadores TAG_HAS, IN_LIFECYCLE, CUSTOM_FIELD_EQ, HAS_INBOUND_IN_LAST_DAYS,
          LAST_ACTIVITY_BEFORE_DAYS, CONSENT_FOR, DND_CHANNEL, SEARCH · AND implícito
        </p>
      </header>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4 bp-card overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-900/10 flex items-center justify-between">
            <span className="text-xs font-mono uppercase tracking-wider text-ink-700/60">
              listas
            </span>
            <button onClick={newOne} className="bp-btn" type="button">
              <Plus className="size-3" /> Nueva
            </button>
          </div>
          <ul>
            {items.map((sl) => (
              <li key={sl.id}>
                <button
                  type="button"
                  onClick={() => pick(sl)}
                  className={[
                    'w-full text-left px-3 py-2 border-b border-ink-900/5 hover:bg-surface-alt',
                    selected?.id === sl.id ? 'bg-blueprint-50' : '',
                  ].join(' ')}
                >
                  <div className="text-sm font-medium">{sl.name}</div>
                  <div className="text-[10px] font-mono text-ink-700/60">
                    {sl.contactCountCached > 0
                      ? `${sl.contactCountCached} contactos`
                      : 'sin contar'}
                    {sl.lastCountedAt &&
                      ` · ${new Date(sl.lastCountedAt).toLocaleDateString('es-ES')}`}
                  </div>
                </button>
              </li>
            ))}
            {items.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-ink-700/50">sin listas</li>
            )}
          </ul>
        </div>

        <div className="col-span-8 bp-card p-4">
          <label className="block mb-3">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              nombre
            </span>
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              filtros JSON (array)
            </span>
            <textarea
              value={draftFilters}
              onChange={(e) => setDraftFilters(e.target.value)}
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs font-mono min-h-[200px]"
              spellCheck={false}
            />
          </label>

          {err && (
            <div className="mt-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {err}
            </div>
          )}

          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => void runPreview()} className="bp-btn" disabled={busy}>
              <Play className="size-3" /> Preview
            </button>
            <button
              type="button"
              onClick={() => void save()}
              className="bp-btn-primary"
              disabled={busy || !draftName.trim()}
            >
              {selected ? 'Guardar cambios' : 'Crear lista'}
            </button>
            {selected && (
              <button
                type="button"
                onClick={() => void remove(selected)}
                className="bp-btn text-red-700 ml-auto"
              >
                <Trash2 className="size-3" /> Eliminar
              </button>
            )}
          </div>

          {preview && (
            <div className="mt-4 border-t border-ink-900/10 pt-3">
              <div className="text-sm font-medium">
                {preview.count} contacto{preview.count === 1 ? '' : 's'} cumplen los filtros
              </div>
              <div className="text-[10px] font-mono text-ink-700/60 mb-2">
                muestra de hasta 10:
              </div>
              <ul className="space-y-1">
                {preview.sample.map((s) => (
                  <li key={s.id}>
                    <Link
                      to={`/marketing/clients/${s.id}`}
                      className="block px-2 py-1 text-xs hover:bg-surface-alt rounded"
                    >
                      <span className="text-blueprint-700">{s.displayName}</span>
                      <span className="font-mono text-ink-700/60 ml-2">
                        {s.email ?? s.phoneE164 ?? ''}
                      </span>
                    </Link>
                  </li>
                ))}
                {preview.sample.length === 0 && (
                  <li className="text-[10px] text-ink-700/40">(sin coincidencias)</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
